
import { useMemo, useState } from 'react'
import {
  ChevronDown,
  Copy as CopyIcon,
  RefreshCw,
  Search,
  ScrollText as ScrollTextIcon,
  X,
} from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { useFormatters } from '@/shared/lib/formatting'
import { cn } from '@/shared/lib/utils'
import {
  DataTable,
  type DataTableColumn,
} from '@/admin/components/data-table'
import { useApiLogDetail, useApiLogs } from '@/admin/hooks/use-api-logs'
import type {
  ApiLogDetail,
  ApiLogFilters,
  ApiLogListRow,
} from '@/admin/types/api-logs'

const METHOD_STYLES: Record<string, string> = {
  GET: 'bg-[#e8f1e7] text-[#2f6f3a]',
  POST: 'bg-[#e3ecf7] text-[#1f4f8a]',
  PUT: 'bg-[#f5ead4] text-[#8a5a1f]',
  PATCH: 'bg-[#f5ead4] text-[#8a5a1f]',
  DELETE: 'bg-[#f5e3e3] text-[#8a2f2f]',
}

function statusTone(code: number | null | undefined): string {
  if (code == null) return 'bg-[var(--bg-soft)] text-[var(--ink-3)]'
  if (code >= 500) return 'bg-[#f5e3e3] text-[#8a2f2f]'
  if (code >= 400) return 'bg-[#f5ead4] text-[#8a5a1f]'
  if (code >= 300) return 'bg-[#eceaf4] text-[#5a5a8a]'
  return 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]'
}

const REPLAY_INLINE =
  'inline-flex items-center gap-1 rounded-[3px] border border-[#ead7a5] bg-[#f7efd9] px-1.5 py-px text-[10px] font-medium text-[#6b5a1f]'

const REPLAY_BADGE =
  'inline-flex items-center gap-1.5 rounded-full border border-[#ead7a5] bg-[#f7efd9] px-2 py-0.5 text-[11px] text-[#6b5a1f]'

/* ─── String + time helpers ───────────────────────────────────────── */

function initials(name: string): string {
  return name
    .replace(/\.$/, '')
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

function formatHMS(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return iso
  }
}

function formatBytes(n: number | null | undefined): string {
  if (n == null) return '-'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(2)} MB`
}

function shortEndpoint(endpoint: string): string {
  return endpoint.replace(/^\/api\/v\d+/, '')
}

/* ─── JSON syntax highlighting ────────────────────────────────────── */

function HighlightedJson({ value }: { value: unknown }) {
  const source = useMemo(() => JSON.stringify(value, null, 2) ?? '', [value])
  const nodes = useMemo(() => tokenize(source), [source])
  return (
    <pre className="m-0 overflow-x-auto bg-[var(--bg)] px-4 py-3.5 font-mono text-[11.5px] leading-[1.65]">
      {nodes}
    </pre>
  )
}

function tokenize(source: string): React.ReactNode[] {
  const out: React.ReactNode[] = []
  const pattern =
    /("(?:[^"\\]|\\.)*")(\s*:)|("(?:[^"\\]|\\.)*")|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false|null)\b|([{}[\],:])/g
  let cursor = 0
  let key = 0
  for (const m of source.matchAll(pattern)) {
    if (m.index! > cursor) out.push(source.slice(cursor, m.index))
    if (m[1]) {
      out.push(
        <span key={key++} className="text-[#2a5aa8]">
          {m[1]}
        </span>,
        <span key={key++} className="text-[var(--ink-3)]">
          {m[2]}
        </span>,
      )
    } else if (m[3]) {
      out.push(
        <span key={key++} className="text-[#2f6f3a]">
          {m[3]}
        </span>,
      )
    } else if (m[4]) {
      out.push(
        <span key={key++} className="text-[#8a5a1f]">
          {m[4]}
        </span>,
      )
    } else if (m[5]) {
      out.push(
        <span key={key++} className="text-[#6b3da8]">
          {m[5]}
        </span>,
      )
    } else if (m[6]) {
      out.push(
        <span key={key++} className="text-[var(--ink-3)]">
          {m[6]}
        </span>,
      )
    }
    cursor = m.index! + m[0].length
  }
  if (cursor < source.length) out.push(source.slice(cursor))
  return out
}

/* ─── Visual primitives ───────────────────────────────────────────── */

function MethodPill({ method }: { method: string }) {
  return (
    <span
      className={cn(
        'inline-flex min-w-[54px] items-center justify-center rounded-[3px] px-[7px] py-[2px] font-mono text-[10.5px] font-medium tracking-[0.02em]',
        METHOD_STYLES[method] || 'bg-[var(--bg-soft)] text-[var(--ink-3)]',
      )}
    >
      {method}
    </span>
  )
}

function StatusPill({ code }: { code: number | null }) {
  if (code == null) return <span className="text-[var(--ink-4)]">-</span>
  return (
    <span
      className={cn(
        'inline-flex min-w-[42px] items-center justify-center rounded-[3px] px-[7px] py-[2px] font-mono text-[11px] font-medium tabular-nums',
        statusTone(code),
      )}
    >
      {code}
    </span>
  )
}

function Avatar({ name }: { name: string }) {
  return (
    <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-[var(--rule)] bg-[var(--bg-soft)] text-[9.5px] font-medium text-[var(--ink-2)]">
      {initials(name)}
    </span>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2.5 border-b border-[var(--rule-soft)] pb-1.5 text-[10px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
      {children}
    </div>
  )
}

function KvRow({
  label,
  children,
  mono = true,
}: {
  label: string
  children: React.ReactNode
  mono?: boolean
}) {
  return (
    <>
      <dt className="py-1 text-[11.5px] font-normal text-[var(--ink-3)]">
        {label}
      </dt>
      <dd
        className={cn(
          'm-0 break-all py-1 text-[12px] text-[var(--ink)]',
          mono && 'font-mono',
        )}
      >
        {children}
      </dd>
    </>
  )
}

/* ─── Filter chip + dropdown ──────────────────────────────────────── */

function FilterChip({
  label,
  active,
  onToggle,
  onClear,
}: {
  label: string
  active: boolean
  onToggle: () => void
  onClear?: () => void
}) {
  return (
    <span
      className={cn(
        'inline-flex h-[26px] items-center gap-1.5 rounded-md border px-2.5 text-[11.5px] font-normal transition-colors',
        active
          ? 'border-[var(--ink)] bg-[var(--ink)] text-white'
          : 'border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-2)] hover:border-[var(--rule-strong)] hover:bg-[var(--bg-hover)]',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="cursor-pointer bg-transparent text-inherit"
      >
        {label}
      </button>
      {active && onClear ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onClear()
          }}
          aria-label="Clear filter"
          className="cursor-pointer text-white/80 hover:text-white"
        >
          <X size={11} />
        </button>
      ) : (
        <ChevronDown
          size={11}
          className={active ? 'text-white/60' : 'text-[var(--ink-4)]'}
        />
      )}
    </span>
  )
}

function DropdownMenu({
  open,
  onClose,
  children,
}: {
  open: boolean
  onClose: () => void
  children: React.ReactNode
}) {
  if (!open) return null
  return (
    <>
      <div className="fixed inset-0 z-10" onClick={onClose} />
      <div className="absolute top-[calc(100%+4px)] left-0 z-20 min-w-[200px] rounded-md border border-[var(--rule)] bg-[var(--bg-card)] py-1 shadow-[0_4px_12px_rgba(0,0,0,0.06)]">
        {children}
      </div>
    </>
  )
}

function DropdownItem({
  children,
  onClick,
  active,
}: {
  children: React.ReactNode
  onClick: () => void
  active?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full cursor-pointer items-center px-3 py-1.5 text-left text-[12px] text-[var(--ink-2)] hover:bg-[var(--bg-hover)]',
        active && 'font-medium text-[var(--ink)]',
      )}
    >
      {children}
    </button>
  )
}

/* ─── Filter bar ──────────────────────────────────────────────────── */

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const
const STATUS_CLASSES = [
  { key: '2xx', label: '2xx · Success' },
  { key: '3xx', label: '3xx · Redirect' },
  { key: '4xx', label: '4xx · Client error' },
  { key: '5xx', label: '5xx · Server error' },
] as const

type FilterKey = 'method' | 'status_class'

function FilterBar({
  filters,
  onChange,
}: {
  filters: ApiLogFilters
  onChange: (next: ApiLogFilters) => void
}) {
  const [openKey, setOpenKey] = useState<FilterKey | null>(null)
  const hasAny = Object.values(filters).some((v) => v)

  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
      <span className="relative inline-flex">
        <FilterChip
          label={filters.method ? `Method: ${filters.method}` : 'Method'}
          active={!!filters.method}
          onToggle={() => setOpenKey(openKey === 'method' ? null : 'method')}
          onClear={() => onChange({ ...filters, method: undefined })}
        />
        <DropdownMenu
          open={openKey === 'method'}
          onClose={() => setOpenKey(null)}
        >
          {METHODS.map((m) => (
            <DropdownItem
              key={m}
              active={filters.method === m}
              onClick={() => {
                onChange({ ...filters, method: m })
                setOpenKey(null)
              }}
            >
              {m}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </span>

      <span className="relative inline-flex">
        <FilterChip
          label={
            filters.status_class ? `Status: ${filters.status_class}` : 'Status'
          }
          active={!!filters.status_class}
          onToggle={() =>
            setOpenKey(openKey === 'status_class' ? null : 'status_class')
          }
          onClear={() => onChange({ ...filters, status_class: undefined })}
        />
        <DropdownMenu
          open={openKey === 'status_class'}
          onClose={() => setOpenKey(null)}
        >
          {STATUS_CLASSES.map((s) => (
            <DropdownItem
              key={s.key}
              active={filters.status_class === s.key}
              onClick={() => {
                onChange({ ...filters, status_class: s.key })
                setOpenKey(null)
              }}
            >
              {s.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </span>

      {hasAny && (
        <button
          type="button"
          onClick={() => onChange({})}
          className="ml-auto cursor-pointer border-0 bg-transparent px-1.5 text-[11.5px] text-[var(--ink-3)] underline decoration-[var(--rule-strong)] underline-offset-[3px] hover:text-[var(--ink)] hover:decoration-[var(--ink-3)]"
        >
          Clear all
        </button>
      )}
    </div>
  )
}

/* ─── List row + table ────────────────────────────────────────────── */

// Header-only column defs; row markup is supplied via DataTable's
// `renderRow` so the selected-row styling (cream bg + left bar) can stay
// row-level. `cell` renderers are never called when `renderRow` is set.
const LOG_COLUMNS: DataTableColumn<ApiLogListRow>[] = [
  { id: 'time', header: 'Time' },
  { id: 'method', header: 'Method' },
  { id: 'endpoint', header: 'Endpoint' },
  { id: 'status', header: 'Status' },
  { id: 'ms', header: 'MS' },
  { id: 'user', header: 'User' },
]

function LogRow({
  row,
  selected,
  onClick,
}: {
  row: ApiLogListRow
  selected: boolean
  onClick: () => void
}) {
  const { locale } = useFormatters()
  return (
    <tr
      onClick={onClick}
      className={cn(
        'cursor-pointer transition-colors',
        selected ? 'bg-[#f5f0e2]' : 'hover:bg-[var(--bg-hover)]',
      )}
    >
      <td
        className={cn(
          'whitespace-nowrap border-b border-[var(--rule-soft)] px-[14px] py-[10px] font-mono text-[12px] tabular-nums text-[var(--ink-3)]',
          selected && 'shadow-[inset_2px_0_0_var(--ink)]',
        )}
      >
        {formatHMS(row.created_at, locale)}
      </td>
      <td className="border-b border-[var(--rule-soft)] px-[14px] py-[10px]">
        <MethodPill method={row.method} />
      </td>
      <td className="max-w-0 border-b border-[var(--rule-soft)] px-[14px] py-[10px]">
        <div className="flex items-center gap-2 overflow-hidden">
          <span className="truncate font-mono text-[12px] text-[var(--ink)]">
            {shortEndpoint(row.endpoint)}
          </span>
          {row.idempotency_replayed && (
            <span className={cn(REPLAY_INLINE, 'flex-shrink-0')}>
              <RefreshCw size={9} /> replay
            </span>
          )}
        </div>
      </td>
      <td className="border-b border-[var(--rule-soft)] px-[14px] py-[10px]">
        <StatusPill code={row.response_code} />
      </td>
      <td className="border-b border-[var(--rule-soft)] px-[14px] py-[10px] text-right font-mono text-[12px] tabular-nums text-[var(--ink-3)]">
        {row.duration_ms ?? '-'}
      </td>
      <td className="overflow-hidden border-b border-[var(--rule-soft)] px-[14px] py-[10px] text-[12px] text-ellipsis whitespace-nowrap text-[var(--ink-2)]">
        <span className="inline-flex items-center gap-2">
          <Avatar name={row.caller.user_display || 'System'} />
          <span className="truncate">
            {row.caller.user_display || 'System'}
          </span>
        </span>
      </td>
    </tr>
  )
}

/* ─── Detail pane ─────────────────────────────────────────────────── */

type DetailTab = 'overview' | 'request' | 'response' | 'headers' | 'curl'
const TABS: { key: DetailTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'request', label: 'Request' },
  { key: 'response', label: 'Response' },
  { key: 'headers', label: 'Headers' },
  { key: 'curl', label: 'cURL' },
]

function TabBar({
  value,
  onChange,
}: {
  value: DetailTab
  onChange: (v: DetailTab) => void
}) {
  return (
    <nav className="flex items-center gap-1 border-b border-[var(--rule)] bg-[var(--bg-card)] px-[22px]">
      {TABS.map(({ key, label }) => {
        const active = value === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={cn(
              'relative cursor-pointer border-0 bg-transparent px-3 py-2.5 font-sans text-[12px] font-medium transition-colors',
              active
                ? 'text-[var(--ink)]'
                : 'text-[var(--ink-3)] hover:text-[var(--ink)]',
            )}
          >
            {label}
            {active && (
              <span className="absolute right-2 -bottom-px left-2 h-[2px] rounded-t-sm bg-[var(--ink)]" />
            )}
          </button>
        )
      })}
    </nav>
  )
}

function CodeBlock({
  label,
  copyText,
  children,
}: {
  label: string
  copyText: string
  children: React.ReactNode
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-[var(--rule)] bg-[var(--bg)]">
      <div className="flex items-center justify-between border-b border-[var(--rule-soft)] bg-[var(--bg-card)] px-3 py-1.5">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
          {label}
        </span>
        <button
          type="button"
          onClick={() => navigator.clipboard.writeText(copyText)}
          className="inline-flex cursor-pointer items-center gap-1 rounded border-0 bg-transparent px-2 py-1 text-[11px] text-[var(--ink-3)] hover:bg-[var(--bg-hover)] hover:text-[var(--ink)]"
        >
          <CopyIcon size={11} /> Copy
        </button>
      </div>
      {children}
    </div>
  )
}

function buildCurl(log: ApiLogDetail): string {
  const lines: string[] = ['# Reproduce this exact request (with your own key)']
  lines.push(`curl -sS "{{BASE_URL}}${log.endpoint}" \\`)
  if (log.method !== 'GET') lines.push(`  -X ${log.method} \\`)
  lines.push(`  -H "Authorization: Token <your-key>" \\`)
  if (log.headers.api_version)
    lines.push(`  -H "Iwarranty-API-Version: ${log.headers.api_version}" \\`)
  if (log.method !== 'GET' && log.method !== 'DELETE')
    lines.push(`  -H "Idempotency-Key: $(uuidgen)" \\`)
  if (log.request.payload) {
    lines.push(`  -H "Content-Type: application/json" \\`)
    lines.push(`  -d '${JSON.stringify(log.request.payload)}'`)
  } else {
    lines[lines.length - 1] = lines[lines.length - 1].replace(/ \\$/, '')
  }
  return lines.join('\n')
}

function KvGrid({ children }: { children: React.ReactNode }) {
  return (
    <dl className="grid grid-cols-[150px_minmax(0,1fr)] gap-x-3.5 gap-y-1">
      {children}
    </dl>
  )
}

function OverviewTab({ log }: { log: ApiLogDetail }) {
  return (
    <div className="space-y-6">
      <section>
        <SectionLabel>Identification</SectionLabel>
        <KvGrid>
          <KvRow label="Request ID">{log.request_id}</KvRow>
          <KvRow label="Idempotency key">
            {log.headers.idempotency_key || (
              <span className="text-[var(--ink-3)]">-</span>
            )}
          </KvRow>
          <KvRow label="API version">
            {log.headers.api_version || (
              <span className="text-[var(--ink-3)]">-</span>
            )}
          </KvRow>
          <KvRow label="Timestamp">{log.created_at}</KvRow>
        </KvGrid>
      </section>

      <section>
        <SectionLabel>Caller</SectionLabel>
        <KvGrid>
          <KvRow label="User" mono={false}>
            <div className="flex items-center gap-2">
              <Avatar name={log.caller.user_display || 'System'} />
              <span className="truncate">{log.caller.user_display}</span>
            </div>
          </KvRow>
          <KvRow label="User Email" mono={false}>
            {log.caller.user_email}
          </KvRow>
          <KvRow label="API key" mono={false}>
            {log.caller.api_key_name || '-'}
          </KvRow>
          <KvRow label="Organisation" mono={false}>
            {log.caller.organisation || '-'}
          </KvRow>
          <KvRow label="IP address">{log.headers.ip_address || '-'}</KvRow>
          <KvRow label="User-Agent">{log.headers.user_agent || '-'}</KvRow>
        </KvGrid>
      </section>

      <section>
        <SectionLabel>Request</SectionLabel>
        <KvGrid>
          <KvRow label="Method" mono={false}>
            <MethodPill method={log.method} />
          </KvRow>
          <KvRow label="Endpoint">{log.endpoint}</KvRow>
          <KvRow label="Size">{formatBytes(log.request.size_bytes)}</KvRow>
        </KvGrid>
      </section>

      <section>
        <SectionLabel>Response</SectionLabel>
        <KvGrid>
          <KvRow label="Status" mono={false}>
            <StatusPill code={log.response_code} />
          </KvRow>
          <KvRow label="Result code">
            {log.result_code || (
              <span className="text-[var(--ink-3)]">
                (populated on 4xx / 5xx)
              </span>
            )}
          </KvRow>
          <KvRow label="Duration">{log.duration_ms ?? '-'} ms</KvRow>
          <KvRow label="Size">{formatBytes(log.response.size_bytes)}</KvRow>
        </KvGrid>
      </section>

      {log.rate_limit.limit != null && (
        <section>
          <SectionLabel>Rate limit at time of call</SectionLabel>
          <KvGrid>
            <KvRow label="Limit">{log.rate_limit.limit} / minute</KvRow>
            <KvRow label="Remaining">{log.rate_limit.remaining}</KvRow>
            <KvRow label="Reset">
              {log.rate_limit.reset
                ? new Date(log.rate_limit.reset * 1000).toISOString()
                : '-'}
            </KvRow>
          </KvGrid>
        </section>
      )}
    </div>
  )
}

function RequestTab({ log }: { log: ApiLogDetail }) {
  const payload = log.request.payload
  const queryParams = log.request.query_params
  const hasQuery = queryParams != null && Object.keys(queryParams).length > 0

  if (!hasQuery && payload == null) {
    return <p className="text-[12px] text-[var(--ink-3)]">No request body or query parameters.</p>
  }
  return (
    <div className="space-y-4">
      {hasQuery ? (
        <div className="space-y-2">
          <SectionLabel>Query parameters</SectionLabel>
          <KvGrid>
            {Object.entries(queryParams!).map(([k, v]) => (
              <KvRow key={k} label={k}>
                {Array.isArray(v) ? v.join(', ') : String(v)}
              </KvRow>
            ))}
          </KvGrid>
        </div>
      ) : null}
      {payload != null ? (
        <CodeBlock
          label={`application/json · ${formatBytes(log.request.size_bytes)}`}
          copyText={JSON.stringify(payload, null, 2)}
        >
          <HighlightedJson value={payload} />
        </CodeBlock>
      ) : null}
    </div>
  )
}

function ResponseTab({ log }: { log: ApiLogDetail }) {
  if (log.response.payload_stored && log.response.payload != null) {
    return (
      <CodeBlock
        label={`application/json · ${formatBytes(log.response.size_bytes)} · ${log.response_code}`}
        copyText={JSON.stringify(log.response.payload, null, 2)}
      >
        <HighlightedJson value={log.response.payload} />
      </CodeBlock>
    )
  }
  return (
    <div className="space-y-3">
      <SectionLabel>Response body not stored for GET requests</SectionLabel>
    </div>
  )
}

function HeadersTab({ log }: { log: ApiLogDetail }) {
  return (
    <div className="space-y-6">
      <section>
        <SectionLabel>Request headers</SectionLabel>
        <KvGrid>
          <KvRow label="Authorization">
            {log.headers.token_hint
              ? `Token ${log.headers.token_hint}•••`
              : '-'}
          </KvRow>
          <KvRow label="Iwarranty-API-Version">
            {log.headers.api_version || '-'}
          </KvRow>
          <KvRow label="Idempotency-Key">
            {log.headers.idempotency_key || '-'}
          </KvRow>
          <KvRow label="User-Agent">{log.headers.user_agent || '-'}</KvRow>
        </KvGrid>
      </section>
      <section>
        <SectionLabel>Response headers</SectionLabel>
        <KvGrid>
          <KvRow label="X-Request-ID">{log.request_id}</KvRow>
          <KvRow label="X-RateLimit-Limit">
            {log.rate_limit.limit ?? '-'}
          </KvRow>
          <KvRow label="X-RateLimit-Remaining">
            {log.rate_limit.remaining ?? '-'}
          </KvRow>
          <KvRow label="X-RateLimit-Reset">
            {log.rate_limit.reset ?? '-'}
          </KvRow>
        </KvGrid>
      </section>
    </div>
  )
}

function DetailPane({
  log,
  onClose,
}: {
  log: ApiLogDetail
  onClose: () => void
}) {
  const { locale } = useFormatters()
  const [tab, setTab] = useState<DetailTab>('overview')
  const curl = useMemo(() => buildCurl(log), [log])

  return (
    <div className="flex h-full flex-col bg-[var(--bg-card)]">
      <div className="px-[22px] pt-[22px]">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2 font-mono text-[13px] text-[var(--ink)]">
              <MethodPill method={log.method} />
              <span className="min-w-0 flex-1 truncate" title={log.endpoint}>
                {log.endpoint}
              </span>
              <StatusPill code={log.response_code} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[var(--ink-3)]">
              {log.caller.user_email ? (
                <span>
                  {log.caller.user_display}{' '}
                </span>
              ) : (
                <span>{log.caller.user_display || 'System'}</span>
              )}
              <span className="text-[var(--ink-4)]">·</span>
              <span>{log.duration_ms ?? '-'} ms</span>
              <span className="text-[var(--ink-4)]">·</span>
              <span>{formatHMS(log.created_at, locale)} UTC</span>
            </div>
            {log.idempotency_replayed && (
              <div className="mt-2">
                <span className={REPLAY_BADGE}>
                  <RefreshCw size={11} /> idempotency replayed
                </span>
              </div>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon-xs"
            onClick={onClose}
            aria-label="Close detail"
            className="flex-shrink-0 text-[var(--ink-3)] hover:text-[var(--ink)]"
          >
            <X size={14} />
          </Button>
        </div>
      </div>

      <div className="mt-4 flex flex-1 flex-col overflow-hidden">
        <TabBar value={tab} onChange={setTab} />
        <div className="flex-1 overflow-y-auto bg-[var(--bg-card)]">
          <div className="px-[22px] py-5">
            {tab === 'overview' && <OverviewTab log={log} />}
            {tab === 'request' && <RequestTab log={log} />}
            {tab === 'response' && <ResponseTab log={log} />}
            {tab === 'headers' && <HeadersTab log={log} />}
            {tab === 'curl' && (
              <CodeBlock label="Reproduce with your own key" copyText={curl}>
                <pre className="m-0 overflow-x-auto bg-[var(--bg)] px-4 py-3.5 font-mono text-[11.5px] leading-[1.65] text-[var(--ink)]">
                  {curl}
                </pre>
              </CodeBlock>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Page ────────────────────────────────────────────────────────── */

export function ApiLogsTab() {
  const [filters, setFilters] = useState<ApiLogFilters>({})
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const effectiveFilters: ApiLogFilters = useMemo(
    () => ({ ...filters, ...(search ? { q: search } : {}) }),
    [filters, search],
  )

  const { data, isLoading } = useApiLogs(effectiveFilters)
  const { data: detail } = useApiLogDetail(selectedId)

  const rows = data?.results ?? []
  const truncated = data?.truncated ?? false
  const count = data?.count ?? 0
  const hasDetail = selectedId !== null

  return (
    <div
      className={cn(
        '-mx-8 -my-6 grid h-[calc(100vh-160px)] overflow-hidden bg-[var(--bg-card)]',
        hasDetail
          ? 'grid-cols-[minmax(0,1.35fr)_minmax(380px,1fr)] 2xl:grid-cols-[minmax(0,1.6fr)_minmax(440px,520px)]'
          : 'grid-cols-[1fr]',
      )}
    >
      <section
        className={cn(
          'flex min-w-0 flex-col overflow-hidden',
          hasDetail && 'border-r border-[var(--rule)]',
        )}
      >
        <header className="border-b border-[var(--rule-soft)] bg-[var(--bg-card)] px-[22px] pt-[22px] pb-3">
          <div className="mb-3.5 flex items-baseline justify-between gap-4">
            <h1 className="font-display m-0 text-[26px] leading-none font-medium tracking-[-0.01em] whitespace-nowrap text-[var(--ink)]">
              API logs
            </h1>
            <span className="inline-flex items-center gap-2.5 whitespace-nowrap text-[12px] tabular-nums text-[var(--ink-3)]">
              <span className="inline-flex items-center gap-1.5 text-[11.5px]">
                <span className="relative inline-block h-[7px] w-[7px] rounded-full bg-[#2f8a4a]">
                  <span className="absolute -inset-[3px] animate-ping rounded-full bg-[#2f8a4a] opacity-35" />
                </span>
                live
              </span>
              <span className="text-[var(--ink-4)]">·</span>
              <span>
                Latest{' '}
                <strong className="font-medium text-[var(--ink)]">
                  {count}
                </strong>{' '}
                calls
              </span>
              {truncated && (
                <span className="text-[var(--ink-4)]">(max)</span>
              )}
            </span>
          </div>

          <div className="relative flex h-[34px] items-center rounded-lg border border-[var(--rule)] bg-[var(--bg)] px-3 transition-colors focus-within:border-[var(--ink-3)] focus-within:bg-[var(--bg-card)]">
            <Search size={14} className="flex-shrink-0 text-[var(--ink-4)]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by request ID, endpoint, email…"
              className="h-auto flex-1 border-0 bg-transparent px-2.5 text-[12.5px] shadow-none focus-visible:ring-0"
            />
          </div>

          <FilterBar filters={filters} onChange={setFilters} />
        </header>

        <div className="flex-1 overflow-y-auto bg-[var(--bg-card)] px-[22px] py-3">
          <DataTable<ApiLogListRow>
            tableId="api-logs"
            columns={LOG_COLUMNS}
            data={rows}
            isLoading={isLoading}
            rowKey="id"
            onRowClick={(r) => setSelectedId(r.id)}
            emptyStateNode={
              <div className="px-[22px] py-16 text-center">
                <p className="m-0 text-[13px] text-[var(--ink-2)]">
                  No API calls yet
                </p>
                <p className="mt-1 text-[11.5px] text-[var(--ink-3)]">
                  Logs appear here within seconds of any public-API call.
                </p>
              </div>
            }
            renderRow={(row) => (
              <LogRow
                key={row.id}
                row={row}
                selected={row.id === selectedId}
                onClick={() => setSelectedId(row.id)}
              />
            )}
          />
        </div>
      </section>

      {hasDetail && (
        <aside className="flex min-w-0 flex-col overflow-hidden bg-[var(--bg)]">
          {detail ? (
            <DetailPane log={detail} onClose={() => setSelectedId(null)} />
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full border border-[var(--rule)] bg-[var(--bg-card)] text-[var(--ink-3)]">
                  <ScrollTextIcon size={18} />
                </div>
                <p className="m-0 text-[12px] text-[var(--ink-3)]">Loading…</p>
              </div>
            </div>
          )}
        </aside>
      )}
    </div>
  )
}
